import { CheckboxProps } from '@chakra-ui/react';
import React, { useState } from 'react';

import { SelectedMarketDatesMap } from '../../core/support/selectedMarketDates';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  HStack,
} from '../../core-ui';
import { GetVendors_season_markets as Market } from '../../queries/__generated__/GetVendors';

type SetSelectedMarketDates = React.Dispatch<
  React.SetStateAction<SelectedMarketDatesMap>
>;

type Props = {
  markets: Array<Market>;
  selectedMarketDates: SelectedMarketDatesMap;
  setSelectedMarketDates: SetSelectedMarketDates;
};

export function BillingExportMarketDateFilter(props: Props) {
  const { markets, selectedMarketDates, setSelectedMarketDates } = props;
  return (
    <>
      {markets.map((market) => (
        <MarketDateCheckboxList
          key={market.id}
          onCheckAll={(marketId) => {
            const oldMarketValue = selectedMarketDates.get(marketId);
            if (!oldMarketValue) {
              return;
            }
            setSelectedMarketDates(
              new Map(selectedMarketDates).set(marketId, {
                selectedDates: new Set(oldMarketValue.market.dates),
                market: oldMarketValue.market,
              }),
            );
          }}
          onUncheckAll={(marketId) => {
            const oldMarketValue = selectedMarketDates.get(marketId);
            if (!oldMarketValue) {
              return;
            }
            setSelectedMarketDates(
              new Map(selectedMarketDates).set(marketId, {
                selectedDates: new Set(),
                market: oldMarketValue.market,
              }),
            );
          }}
          onCheckDate={({ marketId, date }) => {
            const oldMarketValue = selectedMarketDates.get(marketId);
            if (!oldMarketValue) {
              return;
            }
            setSelectedMarketDates(
              new Map(selectedMarketDates).set(marketId, {
                selectedDates: new Set(oldMarketValue.selectedDates).add(date),
                market: oldMarketValue.market,
              }),
            );
          }}
          onUncheckDate={({ marketId, date }) => {
            const oldMarketValue = selectedMarketDates.get(marketId);
            if (!oldMarketValue) {
              return;
            }
            const newDates = new Set(oldMarketValue.selectedDates);
            newDates.delete(date);
            setSelectedMarketDates(
              new Map(selectedMarketDates).set(marketId, {
                selectedDates: newDates,
                market: oldMarketValue.market,
              }),
            );
          }}
          market={market}
          selectedMarketDates={selectedMarketDates}
          setSelectedMarketDates={setSelectedMarketDates}
        />
      ))}
    </>
  );
}

type MarketDateCheckboxListProps = {
  market: Market;
  onCheckAll: (marketId: string) => void;
  onCheckDate: (input: { marketId: string; date: string }) => void;
  onUncheckAll: (marketId: string) => void;
  onUncheckDate: (input: { marketId: string; date: string }) => void;
  selectedMarketDates: SelectedMarketDatesMap;
  setSelectedMarketDates: SetSelectedMarketDates;
};

function MarketDateCheckboxList(props: MarketDateCheckboxListProps) {
  const {
    market,
    onCheckAll,
    onCheckDate,
    onUncheckAll,
    onUncheckDate,
    selectedMarketDates,
  } = props;
  const [index, setIndex] = useState<Array<number>>([]);
  const selectedDateSize =
    selectedMarketDates.get(market.id)?.selectedDates.size ?? 0;
  const numberOfDatesInMarket =
    selectedMarketDates.get(market.id)?.market.dates.length ?? 0;
  return (
    <HStack>
      <Accordion
        allowToggle
        index={index}
        onChange={(newIndex) =>
          typeof newIndex === 'number'
            ? setIndex([newIndex])
            : setIndex(newIndex)
        }
        width="100%"
      >
        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <HStack>
                  <Checkbox
                    isChecked={
                      selectedDateSize > 0 &&
                      selectedDateSize === numberOfDatesInMarket
                    }
                    isIndeterminate={
                      selectedDateSize > 0 &&
                      selectedDateSize !== numberOfDatesInMarket
                    }
                    onChange={(e) => {
                      if (e.target.checked === true) {
                        setIndex([0]);
                        // Set all market dates to checked
                        onCheckAll(market.id);
                      } else {
                        setIndex([]);
                        // Set all market dates to unchecked
                        onUncheckAll(market.id);
                      }
                    }}
                  />
                  <Box>{market.name}</Box>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} maxHeight="100px" overflowY="auto">
            {market.dates.map((date) => {
              const isDateSelected =
                selectedMarketDates.get(market.id)?.selectedDates.has(date) ??
                false;
              return (
                <MarketDateCheckbox
                  key={date}
                  date={date}
                  isDateSelected={isDateSelected}
                  onChange={(e) => {
                    if (e.target.checked === true) {
                      // Set all market dates to checked
                      onCheckDate({ date, marketId: market.id });
                    } else {
                      // Set all market dates to unchecked
                      onUncheckDate({ date, marketId: market.id });
                    }
                  }}
                />
              );
            })}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </HStack>
  );
}

type MarketDateCheckboxProps = {
  date: string;
  isDateSelected: boolean;
  onChange: NonNullable<CheckboxProps['onChange']>;
};

function MarketDateCheckbox(props: MarketDateCheckboxProps) {
  const { date, isDateSelected, onChange } = props;
  const dateWithoutTime = date.split('T')[0] ?? '';
  return (
    <HStack>
      <Checkbox isChecked={isDateSelected} onChange={onChange} />
      <Box>{dateWithoutTime}</Box>
    </HStack>
  );
}
